import type {
  AnswerSourcePath,
  CardData,
  Query,
  ApprovalStatus,
  CitationCollection,
  PerformanceCitation,
} from "@/types";
import type { QaBankMessage, AssistantMessage } from "@/types";
import { CHAT_APP_URL } from "./constants";

const NO_ANSWER_SENTINEL = "NO_ANSWER_FOUND";
const NO_ANSWER_DISPLAY_TEXT = "No answer found";
// Matches the backend's MAX_PICKED_ROWS: a PERFORMANCE query cites at most this many rows.
const MAX_PERFORMANCE_CITATIONS = 6;

/**
 * Transform V1 flat OfficeQueryCitation[] into CitationCollection for card rendering.
 *
 * Mirrors the chat app behaviour: deduplicate document citations by documentId
 * (backend returns one citation per chunk — we show one per document) and cap
 * totals so the card stays concise (chat agent typically cites 2-3 sources).
 */
export function flatCitationsToCitationCollection(citations: Query["citations"]): CitationCollection {
  // QA bank: keep top 3 by score, matching chat's typical output
  const qaCitations = citations
    .filter((c) => c.citationType === "QA_BANK")
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .slice(0, 3)
    .map((c) => ({
      id: c.id,
      score: c.score ?? 0,
      question: c.qaBankQuestion?.text ?? "",
      answer: c.qaBankQuestion?.answers?.[0]?.text ?? "",
      approvalStatus: "APPROVED" as ApprovalStatus,
      approvedAt: null,
      qaBankQuestionId: c.qaBankQuestionId ?? "",
      qaBankAnswerId: "",
    }));

  // Documents: deduplicate by documentId (keep highest-scored chunk per doc), cap at 3
  const docCitationsByDoc = new Map<string, Query["citations"][number]>();
  for (const c of citations.filter((c) => c.citationType === "DOCUMENT")) {
    const docId = c.documentId ?? c.id;
    const existing = docCitationsByDoc.get(docId);
    if (!existing || (c.score ?? 0) > (existing.score ?? 0)) {
      docCitationsByDoc.set(docId, c);
    }
  }
  const documentCitations = [...docCitationsByDoc.values()]
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .slice(0, 3)
    .map((c) => ({
      id: c.id,
      documentId: c.documentId ?? "",
      filename: c.document?.name ?? c.filename ?? "",
      quote: c.quote ?? "",
      score: c.score ?? 0,
      citeId: c.citeId,
      droppedByRefinement: c.droppedByRefinement,
    }));

  // Fund reporting rows: one per cited value, in the order the backend picked them
  const performanceCitations: PerformanceCitation[] = [];
  for (const c of citations) {
    if (c.citationType !== "PERFORMANCE" || !c.performanceRef) continue;
    if (performanceCitations.length === MAX_PERFORMANCE_CITATIONS) break;
    performanceCitations.push({
      id: c.id,
      citeId: c.citeId,
      quote: c.quote ?? "",
      ref: c.performanceRef,
      reportDeleted: !c.performanceFundImportId,
    });
  }

  return { documentCitations, qaBankCitations: qaCitations, performanceCitations };
}

export function performanceCitationsOf(citations: CitationCollection): PerformanceCitation[] {
  return citations.performanceCitations ?? [];
}

/**
 * Deep link to the cited fund report; to the fund alone once the report was deleted. A firm-wide
 * answer may cite a fund of another product, so the citation's product wins over the add-in's.
 */
export function fundReportUrl(productId: string, citation: PerformanceCitation): string {
  const fundProductId = citation.ref.productId || productId;
  const fundUrl = `${CHAT_APP_URL}/products/${fundProductId}/performance/fund-reporting/${citation.ref.fundId}`;
  return citation.reportDeleted ? fundUrl : `${fundUrl}?report=${citation.ref.importId}`;
}

/**
 * Convert Query to CardData
 */
export function queryToCardData(query: Query): CardData {
  const citations = flatCitationsToCitationCollection(query.citations);
  const firstQaBankCitation = citations.qaBankCitations?.[0];
  const sourcePath = query.sourcePath ?? inferSourcePath(query, citations);

  return {
    id: query.id,
    question: query.question,
    answer: displayAnswerForHome(query.answer),
    citations,
    metadata: {
      isDisliked: query.isDisliked,
      dislikeComment: query.dislikeComment ?? undefined,
      approvalStatus: firstQaBankCitation?.approvalStatus,
      searchScore: firstQaBankCitation?.score,
      sourcePath,
      approvedAt: firstQaBankCitation?.approvedAt,
      qaBankCitationId: firstQaBankCitation?.id,
      qaBankQuestionId: firstQaBankCitation?.qaBankQuestionId,
      qaBankAnswerId: firstQaBankCitation?.qaBankAnswerId,
    },
  };
}

function displayAnswerForHome(answer: string | null | undefined): string {
  if (answer?.trim().toUpperCase() === NO_ANSWER_SENTINEL) {
    return NO_ANSWER_DISPLAY_TEXT;
  }
  return answer ?? "";
}

function inferSourcePath(query: Query, citations: CitationCollection): AnswerSourcePath | null {
  if (citations.qaBankCitations.length === 0) {
    if (citations.documentCitations.length > 0) return "document_search";
    return performanceCitationsOf(citations).length > 0 ? "fund_reporting" : null;
  }

  const answer = normalizeAnswerText(query.answer ?? "");
  const exactQaAnswer = citations.qaBankCitations.some(
    (citation) => answer.length > 0 && answer === normalizeAnswerText(citation.answer)
  );

  if (exactQaAnswer) return "qa_direct";
  if (citations.documentCitations.length === 0) return "qa_synthesized";
  return null;
}

/**
 * Convert QaBankMessage to CardData
 */
export function qaBankMessageToCardData(message: QaBankMessage): CardData {
  return {
    id: message.metadata.qaBankCitationId,
    question: message.content.originalQuestion,
    answer: message.content.answer || "",
    citations: message.content.citations || { documentCitations: [], qaBankCitations: [] },
    metadata: {
      approvalStatus: message.metadata.approvalStatus,
      searchScore: message.metadata.searchScore,
      approvedAt: message.metadata.approvedAt,
      qaBankCitationId: message.metadata.qaBankCitationId,
      qaBankQuestionId: message.metadata.qaBankQuestionId,
      qaBankAnswerId: message.metadata.qaBankAnswerId,
    },
  };
}

/**
 * Convert AssistantMessage to CardData
 */
export function assistantMessageToCardData(message: AssistantMessage): CardData {
  return {
    id: `assistant-${Date.now()}`,
    question: message.content.originalQuestion,
    answer: message.content.answer,
    citations: message.content.citations,
  };
}

/**
 * Add citation links to answer text. Numbering follows the citations list:
 * documents, then answer bank, then fund reporting.
 */
export function enhanceAnswerWithCitations(
  answer: string | null | undefined,
  citations: CardData["citations"],
  productId?: string,
  requestId?: string
): string {
  if (!answer) {
    answer = "Answer not found.";
  }

  const requestUrl = productId && requestId ? `${CHAT_APP_URL}/products/${productId}/requests/${requestId}` : null;
  let index = 0;
  const links: string[] = [];

  for (let i = 0; i < citations.documentCitations.length; i++) {
    index++;
    if (requestUrl) links.push(`[[${index}]](${requestUrl})`);
  }
  for (const c of citations.qaBankCitations) {
    index++;
    const qaBankUrl = c.qaBankQuestionId ? `${CHAT_APP_URL}/qa-bank?questionId=${c.qaBankQuestionId}` : requestUrl;
    if (qaBankUrl) links.push(`[[${index}]](${qaBankUrl})`);
  }
  for (const c of performanceCitationsOf(citations)) {
    index++;
    const citationProductId = c.ref.productId || productId;
    if (citationProductId) links.push(`[[${index}]](${fundReportUrl(citationProductId, c)})`);
  }

  if (links.length > 0) {
    return answer.trim() + " " + links.join(" ");
  }

  return answer;
}

/**
 * Check if card has any citations
 */
export function hasCitations(data: CardData): boolean {
  return (
    data.citations.documentCitations.length > 0 ||
    data.citations.qaBankCitations.length > 0 ||
    performanceCitationsOf(data.citations).length > 0 ||
    !!data.citations.reportCitations?.length
  );
}

/**
 * Create chat message history from card data
 */
export function createChatMessageHistory(data: CardData) {
  return [
    {
      role: "user" as const,
      content: {
        text: data.question,
        includesAttachments: false,
      },
    },
    {
      role: "assistant" as const,
      content: {
        originalQuestion: data.question,
        answer: data.answer,
        citations: data.citations,
      },
    },
  ];
}

/**
 * Get header text based on citations
 */
export function getHeaderText(data: CardData): string {
  const hasDocumentCitations = data.citations.documentCitations.length > 0;
  const hasQaBankCitations = data.citations.qaBankCitations.length > 0;
  const hasMultipleQaBankCitations = data.citations.qaBankCitations.length > 1;
  const hasPerformanceCitations = performanceCitationsOf(data.citations).length > 0;
  const sourcePath = data.metadata?.sourcePath;

  if (sourcePath === "qa_direct" || sourcePath === "qa_synthesized") {
    return hasMultipleQaBankCitations ? "Based on your answer bank." : "Found in your answer bank.";
  }

  if (sourcePath === "document_search") return "Based on your documents.";

  if (sourcePath === "fund_reporting" || (hasPerformanceCitations && !hasQaBankCitations && !hasDocumentCitations)) {
    return "Based on your fund reports.";
  }

  if (hasQaBankCitations && !hasDocumentCitations) {
    return hasMultipleQaBankCitations ? "Based on your answer bank." : "Found in your answer bank.";
  }

  return "Based on your documents.";
}

/**
 * Get primary citation URL for header link
 */
export function getPrimaryCitationUrl(data: CardData, productId?: string, requestId?: string): string | null {
  if (!productId || !requestId) return null;

  const hasQaBankCitations = data.citations.qaBankCitations.length > 0;
  const hasDocumentCitations = data.citations.documentCitations.length > 0;
  const performanceCitations = performanceCitationsOf(data.citations);
  const sourcePath = data.metadata?.sourcePath;

  if ((sourcePath === "qa_direct" || sourcePath === "qa_synthesized") && hasQaBankCitations) {
    const first = data.citations.qaBankCitations[0];
    return first.qaBankQuestionId ? `${CHAT_APP_URL}/qa-bank?questionId=${first.qaBankQuestionId}` : null;
  }

  if (sourcePath === "document_search" && hasDocumentCitations) {
    return `${CHAT_APP_URL}/products/${productId}/requests/${requestId}`;
  }

  if (sourcePath === "fund_reporting" && performanceCitations.length > 0) {
    return fundReportUrl(productId, performanceCitations[0]);
  }

  if (hasQaBankCitations && !hasDocumentCitations) {
    const first = data.citations.qaBankCitations[0];
    return first.qaBankQuestionId ? `${CHAT_APP_URL}/qa-bank?questionId=${first.qaBankQuestionId}` : null;
  }

  if (hasDocumentCitations) return `${CHAT_APP_URL}/products/${productId}/requests/${requestId}`;

  if (performanceCitations.length > 0) return fundReportUrl(productId, performanceCitations[0]);

  return null;
}

function normalizeAnswerText(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Match badge, mirroring the web UI's DOCX answer card (aiir-chat match-type.ts):
 * the backend pins exact QA reuse to a citation score of 1.0, so the best QA
 * citation decides Exact vs Partial, and an answer without QA citations is No match.
 * Scores above 1.0 are historical reranker-scale Office scores, never exact.
 * Fund reporting answers never went through QA matching, so they carry no badge.
 */
export function getMatchBadgeInfo(data: CardData): { type: "exact" | "partial" | "none"; text: string } | null {
  if (data.metadata?.sourcePath === "fund_reporting" || performanceCitationsOf(data.citations).length > 0) {
    return null;
  }
  const qaScores = data.citations.qaBankCitations.map((citation) => citation.score);
  if (qaScores.length === 0) {
    const hasAnswer = data.answer.trim() !== "" && data.answer !== NO_ANSWER_DISPLAY_TEXT;
    return hasAnswer ? { type: "none", text: "No match" } : null;
  }
  return qaScores.some((score) => score >= 0.99 && score <= 1)
    ? { type: "exact", text: "Exact Match" }
    : { type: "partial", text: "Partial Match" };
}

/**
 * Get approval badge info
 */
export function getApprovalBadgeInfo(
  status?: ApprovalStatus
): { type: "pending" | "approved" | "rejected"; text: string } | null {
  if (!status) return null;

  const badgeMap = {
    PENDING_APPROVAL: { type: "pending" as const, text: "Pending Approval" },
    APPROVED: { type: "approved" as const, text: "Approved" },
    REJECTED: { type: "rejected" as const, text: "Changes Requested" },
  };

  return badgeMap[status] || null;
}

/**
 * Check if card should show badges in the header
 */
export function shouldShowCardHeader(data: CardData): boolean {
  // Show header if there are citations OR if there are badges to display
  const hasMatchBadge = getMatchBadgeInfo(data) !== null;
  const hasApprovalBadge = getApprovalBadgeInfo(data.metadata?.approvalStatus) !== null;

  return hasCitations(data) || hasMatchBadge || hasApprovalBadge;
}
